import {
  CollapsibleIconButton,
  DataTableColDef,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@chronosphereio/chrono-ui';
import { useDebouncedState } from '@chronosphereio/shared';
import { produce } from 'immer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChangeOrderButtons } from '@/components';
import {
  FormatOverrides,
  NumberFormatThresholdOptionsSchema,
  StringFormatOverrides,
  TableFormatOverrideKind,
  TableOverridesSchema,
  TableSpec,
  TableThresholdOptions,
  UnitOptions,
} from '@/core';
import { UnitSelector } from '@/plugin-editor-components';
import { OptionsEditorProps } from '../../../plugin-system';
import { TableColumnFormatEditor } from './TableColumnFormatEditor';
import { TableColumnStringOverridesEditor } from './TableColumnStringOverridesEditor';
import { TableColumnThresholdsEditor } from './TableColumnThresholdsEditor';
import { DEFAULT_UNIT, sortColumns } from './table-model';
import { getTableFormatOverrideKind, isNumberFormatOverrides, isStringFormatOverrides } from './useFormattedColumns';
import { useTableRowsAndColumns } from './utils';
import { createFormatOverride, getValidFormatKinds } from './utils/format';

const UNIT_EDITOR_WIDTH = 300;

export type TableColumnsSettingsProps = OptionsEditorProps<TableSpec>;

export function TableColumnsSettings(props: TableColumnsSettingsProps) {
  const { onChange, value } = props;

  const { columns, rows } = useTableRowsAndColumns(value);

  // need an internal state of column overrides for editing to work properly
  const [columnOverrides, setColumnOverrides] = useState(value.columnOverrides);
  const [columnOverridesDebounced, setColumnOverridesDebounced] = useDebouncedState(
    columnOverrides,
    setColumnOverrides,
    250
  );

  // All rows are collapsed initially
  const [isExpandedByColumnId, setIsExpandedByColumnId] = useState(
    columns.reduce((lookup: Record<string, boolean>, col: DataTableColDef) => {
      if (col.field) {
        lookup[col.field] = false;
      }
      return lookup;
    }, {})
  );

  // call onChange every time columnOverrides is updated
  useEffect(() => {
    onChange(
      produce(value, (draft: TableSpec) => {
        draft.columnOverrides = columnOverrides;
      })
    );
    // omitting onChange and value from dependency array because otherwise it will cause infinite rerenders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnOverrides]);

  const handleExpandCollapseClick = (columnId: string) => {
    setIsExpandedByColumnId(
      produce(isExpandedByColumnId, (draft) => {
        draft[columnId] = !draft[columnId];
      })
    );
  };

  const handleColumnNameChange = (newName: string, columnId: string) => {
    const nameOverride = {
      value: columnId ?? '',
      properties: {
        displayName: newName,
      },
    };
    setColumnOverridesDebounced(
      produce(columnOverridesDebounced, (overrides) => {
        if (!overrides) return [nameOverride];
        const overrideIndex = overrides.findIndex((override) => override.value === columnId);
        const override = overrides[overrideIndex];
        if (override) {
          override.properties.displayName = newName;
        } else {
          overrides.push(nameOverride);
        }
      })
    );
  };

  const handleVisibilityChange = (checked: boolean, columnId: string) => {
    const hidden = !checked;
    const hiddenOverride = {
      value: columnId ?? '',
      properties: { hidden },
    };
    setColumnOverridesDebounced(
      produce(columnOverridesDebounced, (overrides) => {
        if (!overrides) return [hiddenOverride];
        const overrideIndex = overrides.findIndex((override) => override.value === columnId);
        const override = overrides[overrideIndex];
        if (override) {
          override.properties.hidden = hidden;
        } else {
          overrides.push(hiddenOverride);
        }
      })
    );
  };

  const sortedColumns = useMemo(() => {
    return sortColumns(columns, value.column_order);
  }, [columns, value.column_order]);

  const currentOrder = useMemo(() => {
    return sortedColumns.map((col) => col.field);
  }, [sortedColumns]);

  /**
   * Move column up or down
   */
  const onMoveColumn = (index: number, direction: 'down' | 'up') => {
    const newOrder = currentOrder.slice();
    const column = newOrder[index];
    if (!column) {
      return;
    }

    if (direction === 'down') {
      newOrder.splice(index, 1);
      newOrder.splice(index + 1, 0, column);
    } else {
      newOrder.splice(index, 1);
      newOrder.splice(index - 1, 0, column);
    }

    onChange(
      produce(value, (draft: TableSpec) => {
        draft.column_order = newOrder;
      })
    );
  };

  const handleFormatChange = useCallback(
    (newFormat: FormatOverrides | undefined, columnId: string) => {
      setColumnOverridesDebounced(
        produce(columnOverridesDebounced, (overrides: TableOverridesSchema[]) => {
          if (!overrides) {
            return [
              {
                value: columnId,
                properties: {
                  format: newFormat,
                },
              },
            ];
          }

          const index = overrides.findIndex((o) => o.value === columnId);
          const override = overrides[index];
          if (override) {
            override.properties.format = newFormat;
          } else {
            overrides.push({ value: columnId, properties: { format: newFormat } });
          }
        })
      );
    },
    [setColumnOverridesDebounced, columnOverridesDebounced]
  );

  const renderRows = () => {
    // Unit settings for the entire table
    const tableUnit = value.unit ?? DEFAULT_UNIT;
    // Threshold settings for the entire table
    const tableThresholds = value.thresholds;

    return sortedColumns.map((col, i) => {
      const columnId = col.field;
      const showExpandedEditor = isExpandedByColumnId[columnId];
      const cellStyles = { borderBottom: showExpandedEditor ? 0 : 'auto' };

      let columnOverride: TableOverridesSchema['properties'] = {};
      if (columnOverridesDebounced) {
        const index = columnOverridesDebounced.findIndex((o) => o.value === columnId);
        columnOverride = columnOverridesDebounced[index]?.properties ?? {};
      }

      return (
        <>
          <TableRow key={columnId} aria-label={`${columnId} row`}>
            <TableCell sx={cellStyles}>
              <CollapsibleIconButton
                ariaLabelSubject={columnId}
                onClick={() => {
                  handleExpandCollapseClick(columnId);
                }}
                collapsed={!showExpandedEditor}
              />
            </TableCell>
            <TableCell sx={cellStyles}>
              <Typography maxWidth={200} sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                {columnId}
              </Typography>
            </TableCell>
            <TableCell sx={cellStyles}>
              <TextField
                fullWidth
                multiline
                value={columnOverride.displayName ?? col.headerName}
                onChange={(e) => handleColumnNameChange(e.target.value, columnId)}
                inputProps={{ 'aria-label': `${columnId}` }}
              />
            </TableCell>
            <TableCell sx={cellStyles}>
              <Switch
                checked={!columnOverride.hidden}
                onChange={(e) => handleVisibilityChange(e.target.checked, columnId)}
                aria-label={columnId}
              />
            </TableCell>
            <TableCell sx={cellStyles}>
              <ChangeOrderButtons
                isFirst={i === 0}
                isLast={sortedColumns.length === i + 1}
                onMoveUp={() => onMoveColumn(i, 'up')}
                onMoveDown={() => onMoveColumn(i, 'down')}
              />
            </TableCell>
          </TableRow>
          {showExpandedEditor && (
            <ExpandedColumnEditor
              columnId={columnId}
              tableSettings={{
                unit: tableUnit,
                thresholds: tableThresholds,
              }}
              onFormatChange={handleFormatChange}
              formatOverrides={columnOverride.format}
              formatKind={getTableFormatOverrideKind(col.type)}
              rows={rows}
            />
          )}
        </>
      );
    });
  };

  return (
    <TableContainer>
      <Table aria-label="table columns">
        <TableHead>
          <TableRow>
            {/* Space for the toggle that expands + collapses rows */}
            <TableCell width={5}></TableCell>
            <TableCell width={UNIT_EDITOR_WIDTH + 8}>Name</TableCell>
            <TableCell>Display name</TableCell>
            <TableCell width={100}>Visibility</TableCell>
            <TableCell width={100}>Order</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderRows()}</TableBody>
      </Table>
    </TableContainer>
  );
}

interface TableGlobalSettings {
  unit: UnitOptions;
  thresholds?: NumberFormatThresholdOptionsSchema;
}
interface ExpandedEditorProps {
  columnId: string;
  formatOverrides?: FormatOverrides;
  tableSettings: TableGlobalSettings;
  onFormatChange: (newFormat: FormatOverrides | undefined, columnId: string) => void;
  formatKind: TableFormatOverrideKind;
  rows: Array<Record<string, unknown>>;
}
const ExpandedColumnEditor = (props: ExpandedEditorProps) => {
  const effectiveFormat = props.formatOverrides?.kind ?? props.formatKind;

  if (effectiveFormat === TableFormatOverrideKind.Number) {
    return <ExpandedNumberColumnEditor {...props} />;
  }
  if (effectiveFormat === TableFormatOverrideKind.Time) {
    return <ExpandedTimeColumnEditor {...props} />;
  }
  return <ExpandedStringColumnEditor {...props} />;
};

const ExpandedStringColumnEditor = ({
  columnId,
  formatOverrides,
  onFormatChange,
  formatKind,
  rows,
}: ExpandedEditorProps) => {
  const stringOverrides: StringFormatOverrides = isStringFormatOverrides(formatOverrides)
    ? formatOverrides
    : { kind: TableFormatOverrideKind.String };

  const handleOverridesChange = useCallback(
    (overrides: StringFormatOverrides) => {
      onFormatChange(overrides, columnId);
    },
    [onFormatChange, columnId]
  );

  const handleFormatKindChange = (newKind: TableFormatOverrideKind | undefined) => {
    const newFormat = createFormatOverride(newKind);
    onFormatChange(newFormat, columnId);
  };

  const validKinds = getValidFormatKinds(columnId, rows, formatKind);
  const effectiveKind = formatOverrides?.kind ?? formatKind;
  // Ensure currently applied format is always in the list so dropdown shows current selection
  const validKindsWithCurrent = validKinds.includes(effectiveKind) ? validKinds : [...validKinds, effectiveKind];
  const isOverriddenWithTimeFormat = effectiveKind === TableFormatOverrideKind.Time;

  return (
    <TableRow key={`${columnId}-expanded`}>
      <TableCell colSpan={5}>
        <Stack paddingLeft={7} spacing={2}>
          <TableColumnFormatEditor
            format={effectiveKind}
            originalFormat={formatKind}
            validFormats={validKindsWithCurrent}
            onFormatChange={handleFormatKindChange}
          />
          {!isOverriddenWithTimeFormat && (
            <TableColumnStringOverridesEditor overrides={stringOverrides} onOverridesChange={handleOverridesChange} />
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const ExpandedNumberColumnEditor = ({
  columnId,
  formatOverrides,
  tableSettings: { unit: tableUnit, thresholds: tableThresholds },
  onFormatChange,
  formatKind,
  rows,
}: ExpandedEditorProps) => {
  const handleUnitChange = useCallback(
    (newUnit: UnitOptions) => {
      onFormatChange(
        produce(formatOverrides, (draftFormatOverride) => {
          if (draftFormatOverride && isNumberFormatOverrides(draftFormatOverride)) {
            draftFormatOverride.unit = newUnit;
          } else {
            return {
              kind: TableFormatOverrideKind.Number,
              unit: newUnit,
            };
          }
        }),
        columnId
      );
    },
    [columnId, onFormatChange, formatOverrides]
  );

  const handleThresholdsChange = useCallback(
    (newThresholds: TableThresholdOptions) => {
      onFormatChange(
        produce(formatOverrides, (draftFormatOverrides) => {
          if (draftFormatOverrides && isNumberFormatOverrides(draftFormatOverrides)) {
            draftFormatOverrides.thresholds = newThresholds;
          } else {
            return {
              kind: TableFormatOverrideKind.Number,
              thresholds: newThresholds,
            };
          }
        }),
        columnId
      );
    },
    [onFormatChange, columnId, formatOverrides]
  );

  const handleFormatKindChange = (newKind: TableFormatOverrideKind | undefined) => {
    const newFormat = createFormatOverride(newKind);
    onFormatChange(newFormat, columnId);
  };

  const validKinds = getValidFormatKinds(columnId, rows, formatKind);
  const effectiveKind = formatOverrides?.kind ?? formatKind;
  // Ensure currently applied format is always in the list so dropdown shows current selection
  const validKindsWithCurrent = validKinds.includes(effectiveKind) ? validKinds : [...validKinds, effectiveKind];

  return (
    <TableRow key={`${columnId}-expanded`}>
      <TableCell colSpan={5}>
        <Stack paddingLeft={7} paddingY={1} spacing={2}>
          <TableColumnFormatEditor
            format={effectiveKind}
            originalFormat={formatKind}
            validFormats={validKindsWithCurrent}
            onFormatChange={handleFormatKindChange}
          />
          <Stack spacing={1} width={UNIT_EDITOR_WIDTH}>
            <Typography variant="h4">Unit</Typography>
            <UnitSelector
              value={
                isNumberFormatOverrides(formatOverrides) && formatOverrides.unit ? formatOverrides.unit : tableUnit
              }
              onChange={handleUnitChange}
            />
          </Stack>
          <Typography variant="h4">Color and message</Typography>
          <TableColumnThresholdsEditor
            thresholds={
              isNumberFormatOverrides(formatOverrides) && formatOverrides.thresholds
                ? formatOverrides.thresholds
                : tableThresholds
            }
            onChange={handleThresholdsChange}
          />
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const ExpandedTimeColumnEditor = ({
  columnId,
  formatOverrides,
  onFormatChange,
  formatKind,
  rows,
}: ExpandedEditorProps) => {
  const handleFormatKindChange = (newKind: TableFormatOverrideKind | undefined) => {
    const newFormat = createFormatOverride(newKind);
    onFormatChange(newFormat, columnId);
  };

  const validKinds = getValidFormatKinds(columnId, rows, formatKind);
  const effectiveKind = formatOverrides?.kind ?? formatKind;
  // Ensure currently applied format is always in the list so dropdown shows current selection
  const validKindsWithCurrent = validKinds.includes(effectiveKind) ? validKinds : [...validKinds, effectiveKind];

  return (
    <TableRow key={`${columnId}-expanded`}>
      <TableCell colSpan={5}>
        <Stack paddingLeft={7} spacing={2}>
          <TableColumnFormatEditor
            format={effectiveKind}
            originalFormat={formatKind}
            validFormats={validKindsWithCurrent}
            onFormatChange={handleFormatKindChange}
          />
        </Stack>
      </TableCell>
    </TableRow>
  );
};
